<template>
  <div>
    <div
      v-for="activity in order.orderActivities"
      :key="activity.id"
    >
      <b-row

        class="match-height"
      >
        <b-col :md="hasLocationData(activity) ? 7 : 12">
          <b-card
            no-title
            no-footer
          >

            <div v-if="activity.type === 'status_change'">
              <h5 class="mb-50">
                {{ $t('order.activities.status-change.title') }}:
                <span class="lowercase">{{ $t(`order.status.${activity.new}`) }}</span>
              </h5>
              <p>
                {{ $t('order.activities.status-change.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                {{ $t('order.activities.status-change.changed-order-status') }}
                <br>
                {{ $t('order.activities.status-change.previous-status') }}
                <i>{{ $t(`order.status.${activity.old}`) }}</i>,
                {{ $t('order.activities.status-change.new-status') }}
                <i>{{ $t(`order.status.${activity.new}`) }}</i>
                <template v-if="activity.patientInfo">
                  <br>
                  {{ $t('order.activities.status-change.patient') }}
                  <i>{{ formatPatient(activity.patientInfo) }}</i>
                </template>
                <template v-if="activity.details && activity.details.reason">
                  <br>
                  {{ $t(getStatusChangeReasonLabelKey(activity)) }}
                  <i>{{ activity.details.reason }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'team_change'">
              <h5
                v-if="activity.new && activity.old"
                class="mb-50"
              >
                {{ $t('order.activities.team-change.change-title') }}
              </h5>
              <h5
                v-else-if="activity.new"
                class="mb-50"
              >
                {{ $t('order.activities.team-change.assignment-title') }}
              </h5>
              <h5
                v-else
                class="mb-50"
              >
                {{ $t('order.activities.team-change.reassignment-title') }}
              </h5>
              <p>
                {{ $t('order.activities.team-change.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.old">
                  {{ $t('order.activities.team-change.unassigned') }}
                  <strong>{{ activity.old }}</strong>
                </span>
                <span v-if="activity.new && activity.old">
                  {{ $t('order.activities.team-change.and') }}
                </span>
                <span v-if="activity.new">
                  {{ $t('order.activities.team-change.assigned') }}
                  <strong>{{ activity.new }}</strong>
                </span>
              </p>
            </div>

            <div v-else-if="activity.type === 'team_change_auto'">
              <h5
                v-if="activity.new && activity.old"
                class="mb-50"
              >
                {{ $t('order.activities.team-change.change-title') }}
              </h5>
              <h5
                v-else-if="activity.new"
                class="mb-50"
              >
                {{ $t('order.activities.team-change.automation-title') }}
              </h5>
              <p>
                {{ $t('order.planning.team-definition') }}
                <strong>{{ activity.new || getAutoAssignmentSourceLabel(activity) }}</strong>
                <template v-if="getPlanningAssignmentAt(activity)">
                  <br>
                  {{ $t('order.planning.assignment-at') }}
                  <i>{{ formatPlanningOffset(getPlanningAssignmentAt(activity)) }}</i>
                </template>
                <template v-if="hasPlanningDuration(activity)">
                  <br>
                  {{ $t('order.planning.duration-minutes') }}
                  <i>{{ formatPlanningDuration(getPlanningDurationMinutes(activity)) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'start_at_change'">
              <h5 class="mb-50">
                {{ $t('order.activities.start-at-change.title') }}
              </h5>
              <p>
                {{ $t('order.activities.start-at-change.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <template v-if="activity.old">
                  <br>
                  {{ $t('order.activities.start-at-change.previous') }}
                  <i>{{ transformDatetimeToHumanReadablePretty(activity.old) }}</i>
                </template>
                <template v-if="activity.new">
                  <br>
                  {{ $t('order.activities.start-at-change.new') }}
                  <i>{{ transformDatetimeToHumanReadablePretty(activity.new) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'order_planning_create'">
              <h5 class="mb-50">
                {{ $t('order.planning.title') }}
              </h5>
              <p>
                <template v-if="activity.new">
                  {{ $t('order.planning.team-definition') }}
                  <strong>{{ activity.new }}</strong>
                </template>
                <template v-if="activity.user">
                  <br>
                  {{ $t('order.activities.team-change.user') }}
                  <i>{{ formatUser(activity.user) }}</i>
                </template>
                <template v-if="getPlanningAssignmentAt(activity)">
                  <br>
                  {{ $t('order.planning.assignment-at') }}
                  <i>{{ formatPlanningOffset(getPlanningAssignmentAt(activity)) }}</i>
                </template>
                <template v-if="hasPlanningDuration(activity)">
                  <br>
                  {{ $t('order.planning.duration-minutes') }}
                  <i>{{ formatPlanningDuration(getPlanningDurationMinutes(activity)) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'order_planning_remove'">
              <h5 class="mb-50">
                {{ $t('order.planning.remove-action') }}
              </h5>
              <p>
                <template v-if="activity.old">
                  {{ $t('order.planning.team-definition') }}
                  <strong>{{ activity.old }}</strong>
                </template>
                <template v-if="activity.user">
                  <br>
                  {{ $t('order.activities.team-change.user') }}
                  <i>{{ formatUser(activity.user) }}</i>
                </template>
                <template v-if="getPlanningAssignmentAt(activity)">
                  <br>
                  {{ $t('order.planning.assignment-at') }}
                  <i>{{ formatPlanningOffset(getPlanningAssignmentAt(activity)) }}</i>
                </template>
                <template v-if="hasPlanningDuration(activity)">
                  <br>
                  {{ $t('order.planning.duration-minutes') }}
                  <i>{{ formatPlanningDuration(getPlanningDurationMinutes(activity)) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'order_planning_update'">
              <h5 class="mb-50">
                {{ $t('order.activities.team-change.change-title') }}
              </h5>
              <p>
                <template v-if="activity.user">
                  {{ $t('order.activities.team-change.user') }}
                  <i>{{ formatUser(activity.user) }}</i>
                  <br>
                </template>
                <template v-if="activity.old">
                  {{ $t('order.activities.team-change.unassigned') }}
                  <strong>{{ activity.old }}</strong>
                </template>
                <template v-if="activity.new && activity.old">
                  {{ $t('order.activities.team-change.and') }}
                </template>
                <template v-if="activity.new">
                  {{ $t('order.activities.team-change.assigned') }}
                  <strong>{{ activity.new }}</strong>
                </template>
                <template v-if="getPlanningAssignmentAt(activity)">
                  <br>
                  {{ $t('order.planning.assignment-at') }}
                  <i>{{ formatPlanningOffset(getPlanningAssignmentAt(activity)) }}</i>
                </template>
                <template v-if="hasPlanningDuration(activity)">
                  <br>
                  {{ $t('order.planning.duration-minutes') }}
                  <i>{{ formatPlanningDuration(getPlanningDurationMinutes(activity)) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'team_transfer'">
              <h5 class="mb-50">
                {{ $t('order.activities.team-change.transfer-title') }}
              </h5>
              <p>
                {{ $t('order.activities.team-change.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.old">
                  {{ $t('order.activities.team-change.unassigned') }}
                  <strong>{{ activity.old }}</strong>
                </span>
                <span v-if="activity.new && activity.old">
                  {{ $t('order.activities.team-change.and') }}
                </span>
                <span v-if="activity.new">
                  {{ $t('order.activities.team-change.assigned') }}
                  <strong>{{ activity.new }}</strong>
                </span>
                <template v-if="activity.details && activity.details.transferReason">
                  <br>
                  {{ $t('order.activities.team-change.transfer-reason') }}
                  <i>{{ activity.details.transferReason }}</i>
                </template>
                <template v-if="activity.details && activity.details.orderStatus">
                  <br>
                  {{ $t('order.activities.team-change.transfer-status') }}
                  <i>{{ $t(`order.status.${activity.details.orderStatus}`) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'team_release'">
              <h5 class="mb-50">
                {{ $t('order.activities.team-change.release-title') }}
              </h5>
              <p>
                {{ $t('order.activities.team-change.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.old">
                  {{ $t('order.activities.team-change.unassigned') }}
                  <strong>{{ activity.old }}</strong>
                </span>
                <template v-if="activity.details && activity.details.transferReason">
                  <br>
                  {{ $t('order.activities.team-change.release-reason') }}
                  <i>{{ activity.details.transferReason }}</i>
                </template>
                <template v-if="activity.details && activity.details.orderStatus">
                  <br>
                  {{ $t('order.activities.team-change.transfer-status') }}
                  <i>{{ $t(`order.status.${activity.details.orderStatus}`) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'supporting_team_added'">
              <h5 class="mb-50">
                {{ $t('order.activities.supporting-team.added-title') }}
              </h5>
              <p>
                {{ $t('order.activities.supporting-team.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.new">
                  {{ $t('order.activities.supporting-team.added-description') }}
                  <strong>{{ activity.new }}</strong>
                </span>
                <template v-if="activity.details && activity.details.orderStatus">
                  <br>
                  {{ $t('order.activities.supporting-team.order-status') }}
                  <i>{{ $t(`order.status.${activity.details.orderStatus}`) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'supporting_team_released'">
              <h5 class="mb-50">
                {{ $t('order.activities.supporting-team.released-title') }}
              </h5>
              <p>
                {{ $t('order.activities.supporting-team.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.old">
                  {{ $t('order.activities.supporting-team.released-description') }}
                  <strong>{{ activity.old }}</strong>
                </span>
              </p>
            </div>

            <div v-else-if="activity.type === 'supporting_team_promoted'">
              <h5 class="mb-50">
                {{ $t('order.activities.supporting-team.promoted-title') }}
              </h5>
              <p>
                {{ $t('order.activities.supporting-team.user') }}
                <i>{{ formatUser(activity.user) }}</i>
                <span v-if="activity.new">
                  {{ $t('order.activities.supporting-team.promoted-description') }}
                  <strong>{{ activity.new }}</strong>
                </span>
                <template v-if="activity.old">
                  <br>
                  {{ $t('order.activities.supporting-team.previous-master') }}
                  <strong>{{ activity.old }}</strong>
                </template>
                <template v-if="activity.details && activity.details.reason">
                  <br>
                  {{ $t('order.activities.supporting-team.reason') }}
                  <i>{{ activity.details.reason }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'route_optimization_auto'">
              <h5 class="mb-50">
                {{ $t('order.activities.route-optimization.title') }}
              </h5>
              <p>
                {{ $t('order.activities.route-optimization.description') }}
                <template v-if="activity.user">
                  <br>
                  {{ $t('order.activities.route-optimization.user') }}
                  <i>{{ formatUser(activity.user) }}</i>
                </template>
                <template v-if="activity.old && activity.new">
                  <br>
                  {{ $t('order.activities.route-optimization.transition') }}
                  <i>{{ $t(`order.status.${activity.old}`) }}</i>
                  ->
                  <i>{{ $t(`order.status.${activity.new}`) }}</i>
                </template>
              </p>
            </div>

            <div v-else-if="activity.type === 'route_optimization_manual'">
              <h5 class="mb-50">
                {{ $t('order.activities.route-optimization.manual-title') }}
              </h5>
              <p>
                {{ $t('order.activities.route-optimization.manual-description') }}
                <template v-if="activity.user">
                  <br>
                  {{ $t('order.activities.route-optimization.user') }}
                  <i>{{ formatUser(activity.user) }}</i>
                </template>
              </p>
            </div>

            <small class="float-right mt-50">
              {{ $t('order.activities.activity-time') }}:
              {{ transformDatetimeToHumanReadablePretty(activity.createdAt) }}
            </small>
          </b-card>
        </b-col>

        <b-col
          v-if="hasLocationData(activity)"
          md="5"
        >
          <b-card
            no-body
            no-title
            no-footer
            class="h-100 bg-light-danger"
          >
            <iframe
              class="border-0 h-100 rounded"
              style="min-height: 150px;"
              :src="getMapUrl(activity.latitude, activity.longitude)"
              allowfullscreen
            >
              {{ activity.latitude }}, {{ activity.longitude }}
            </iframe>
          </b-card>
        </b-col>
      </b-row>

    </div>
  </div>
</template>
<script>

import { transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import { formatPlanningMinutes } from '@/helpers/orderPlanning'
import { sprintf } from 'printj'
import { formatActivityPatient } from './helpers/activityPatient'

export default {
  computed: {
    order() {
      return this.$store.getters['order/getOrderData']
    },
  },
  methods: {
    transformDatetimeToHumanReadablePretty,
    sprintf,
    formatUser(user) {
      if (!user) {
        return '-'
      }
      const surname = user.surname || '-'
      const name = user.name || ''
      return `${surname} ${name}`.trim()
    },
    formatPatient(patient) {
      return formatActivityPatient(patient)
    },
    getStatusChangeReasonLabelKey(activity) {
      return activity?.details?.reasonRequirementMode === 'delay'
        ? 'order.activities.status-change.delay-reason'
        : 'order.activities.status-change.reason'
    },
    hasLocationData(activity) {
      return Boolean(activity?.latitude || activity?.longitude)
    },
    getPlanningDetails(activity) {
      const consumedPlanning = activity?.details?.consumedPlanning
      if (consumedPlanning) {
        return consumedPlanning
      }

      const afterPlanning = activity?.details?.after
      if (afterPlanning) {
        return afterPlanning
      }

      return {
        assignmentOffsetMinutes: activity?.details?.plannedAssignmentOffsetMinutes ?? null,
        durationMinutes: activity?.details?.plannedDurationMinutes ?? null,
      }
    },
    getAutoAssignmentSourceLabel(activity) {
      const planning = this.getPlanningDetails(activity)
      const teamDefinitionCode = planning?.teamDefinitionCode || activity?.details?.teamDefinitionCode

      return teamDefinitionCode || '-'
    },
    getPlanningAssignmentAt(activity) {
      return this.getPlanningDetails(activity)?.assignmentOffsetMinutes ?? null
    },
    getPlanningDurationMinutes(activity) {
      return this.getPlanningDetails(activity)?.durationMinutes ?? null
    },
    hasPlanningDuration(activity) {
      return this.getPlanningDurationMinutes(activity) !== null
    },
    formatPlanningDuration(durationMinutes) {
      if (typeof durationMinutes !== 'number') {
        return '-'
      }

      const hours = Math.floor(durationMinutes / 60)
      const minutes = durationMinutes % 60

      return `${hours}h ${minutes.toString().padStart(2, '0')}m`
    },
    formatPlanningOffset(offsetMinutes) {
      return formatPlanningMinutes(offsetMinutes) || '-'
    },
    getMapUrl(latitude, longitude) {
      const queryParams = {
        key: 'AIzaSyB5zbQZqenSwG_xowvr2lQ4_HfU_ZD-CQY',
        q: `${latitude},${longitude}`,
      }

      return `https://www.google.com/maps/embed/v1/place?${Object.keys(queryParams).map(key => `${key}=${queryParams[key]}`).join('&')}`
    },
  },
}
</script>
